import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Modal from '../../components/ui/Modal';
import Button from '../../components/ui/Button';
import CategoryBadge from '../../components/candidates/CategoryBadge';
import { copyWorkflowSteps } from '../../api/workflow';
import { pushToast } from '../../components/ui/toastBus';

// Copy several selected steps at once into other workflows — same per-step behavior as
// CopyWorkflowStepModal (checklist/documents/fields copied, group placement isn't; an existing
// step in the target keeps its position and just has its configuration overwritten).
export default function BulkCopyWorkflowStepsModal({ open, onClose, onCopied, steps, categories, sourceCategoryId }) {
  const queryClient = useQueryClient();
  const [selectedIds, setSelectedIds] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setSelectedIds([]);
      setError('');
    }
  }, [open]);

  const targets = (categories || []).filter((c) => c.id !== sourceCategoryId);
  const allSelected = targets.length > 0 && selectedIds.length === targets.length;

  const toggle = (id) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const toggleAll = () => setSelectedIds(allSelected ? [] : targets.map((c) => c.id));

  const mutation = useMutation({
    // A single request for all selected steps, so the whole bulk copy runs in one backend
    // transaction — firing one request per step in parallel let concurrent inserts race on
    // each target's next sort_order, silently dropping steps and scrambling their order.
    mutationFn: () =>
      copyWorkflowSteps(
        steps.map((step) => step.id),
        selectedIds
      ),
    onSuccess: () => {
      pushToast({
        type: 'success',
        message: `${steps.length} step${steps.length === 1 ? '' : 's'} copied to ${selectedIds.length} workflow${selectedIds.length === 1 ? '' : 's'}.`,
      });
      queryClient.invalidateQueries({ queryKey: ['workflow-categories'] });
      onCopied();
    },
    onError: (err) =>
      setError(
        err.response?.data?.message ||
          Object.values(err.response?.data?.errors || {})[0]?.[0] ||
          'Could not copy the selected steps.'
      ),
  });

  const handleCopy = () => {
    setError('');
    if (selectedIds.length === 0) {
      setError('Pick at least one workflow.');
      return;
    }
    mutation.mutate();
  };

  if (!steps || steps.length === 0) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Copy ${steps.length} step${steps.length === 1 ? '' : 's'} to other workflows`}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleCopy} loading={mutation.isPending} disabled={selectedIds.length === 0}>
            Copy to {selectedIds.length || ''} workflow{selectedIds.length === 1 ? '' : 's'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <p className="text-sm text-gray-500">
            Each step's checklist, required documents and fields are copied. Group placement isn't — copied steps
            land ungrouped at the end. A workflow that already has one of these steps keeps its position and has
            its configuration overwritten.
          </p>
          <p className="mt-2 text-xs text-gray-500">
            Steps: {steps.map((s) => s.title).join(', ')}
          </p>
        </div>

        <div className="flex items-center justify-between">
          <p className="text-xs font-medium text-gray-500">Workflows</p>
          <button type="button" onClick={toggleAll} className="text-xs text-primary hover:underline">
            {allSelected ? 'Clear all' : 'Select all'}
          </button>
        </div>

        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {targets.map((category) => {
            const active = selectedIds.includes(category.id);
            return (
              <button
                key={category.id}
                type="button"
                onClick={() => toggle(category.id)}
                aria-pressed={active}
                className={`rounded-lg border-2 p-3 text-left transition-colors ${
                  active ? 'border-primary bg-primary-50' : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-gray-900">{category.code}</p>
                  <span
                    className={`flex h-4 w-4 items-center justify-center rounded-full border text-[10px] ${
                      active ? 'border-primary bg-primary text-white' : 'border-gray-300 text-transparent'
                    }`}
                  >
                    ✓
                  </span>
                </div>
                <div className="mt-1">
                  <CategoryBadge category={category} />
                </div>
                <p className="mt-2 text-xs text-gray-500">{category.steps.length} steps</p>
              </button>
            );
          })}
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}
      </div>
    </Modal>
  );
}
